// Tweaks panel — palette, fonts, hero layout, section order
const { useState, useEffect, useRef } = React;

const PALETTES = {
  wisteria: {
    label: 'Wisteria & olive',
    cream: 'oklch(97% 0.012 85)',
    creamDeep: 'oklch(94% 0.02 85)',
    ink: 'oklch(24% 0.02 85)',
    inkSoft: 'oklch(38% 0.02 85)',
    wisteria: 'oklch(66% 0.10 305)',
    wisteriaSoft: 'oklch(88% 0.045 305)',
    olive: 'oklch(48% 0.06 120)',
    oliveSoft: 'oklch(78% 0.035 120)',
  },
  lavender: {
    label: 'Lavande & sauge',
    cream: 'oklch(97% 0.008 90)',
    creamDeep: 'oklch(93% 0.018 90)',
    ink: 'oklch(26% 0.02 270)',
    inkSoft: 'oklch(42% 0.02 270)',
    // Violet accordé aux glycines aquarelle du header (#9b8ab5 / #715e90)
    wisteria: '#806ba6',
    wisteriaSoft: '#d9cee9',
    // Accent secondaire : taupe chaud (greige), quasi neutre — laisse le lavande mener.
    olive: '#9a8f7e',
    oliveSoft: '#cfc8bb',
  },
  peach: {
    label: 'Pêche & terracotta',
    cream: 'oklch(97% 0.012 70)',
    creamDeep: 'oklch(93% 0.03 65)',
    ink: 'oklch(28% 0.04 40)',
    inkSoft: 'oklch(44% 0.03 40)',
    wisteria: 'oklch(68% 0.13 35)',
    wisteriaSoft: 'oklch(88% 0.05 35)',
    olive: 'oklch(55% 0.07 95)',
    oliveSoft: 'oklch(80% 0.04 95)',
  },
  mimosa: {
    label: 'Mimosa & bleu doux',
    cream: 'oklch(97% 0.015 95)',
    creamDeep: 'oklch(93% 0.035 95)',
    ink: 'oklch(26% 0.03 240)',
    inkSoft: 'oklch(42% 0.03 240)',
    wisteria: 'oklch(70% 0.10 230)',
    wisteriaSoft: 'oklch(88% 0.045 230)',
    olive: 'oklch(62% 0.11 85)',
    oliveSoft: 'oklch(85% 0.05 85)',
  }
};

const FONT_PAIRS = {
  cormorant: {
    label: 'Cormorant + Inter',
    sub: 'Classique',
    display: "'Cormorant Garamond', Georgia, serif",
    body: "'Inter', -apple-system, system-ui, sans-serif",
    gf: 'Cormorant+Garamond:ital,wght@0,400;0,500;1,400;1,500&family=Inter:wght@400;500'
  },
  playfair: {
    label: 'Playfair + Work Sans',
    sub: 'Éditorial',
    display: "'Playfair Display', Georgia, serif",
    body: "'Work Sans', -apple-system, sans-serif",
    gf: 'Playfair+Display:ital,wght@0,400;0,500;1,400&family=Work+Sans:wght@400;500'
  },
  dmserif: {
    label: 'DM Serif + DM Sans',
    sub: 'Moderne',
    display: "'DM Serif Display', Georgia, serif",
    body: "'DM Sans', -apple-system, sans-serif",
    gf: 'DM+Serif+Display:ital@0;1&family=DM+Sans:wght@400;500'
  },
  libre: {
    label: 'Libre Caslon + Libre Franklin',
    sub: 'Traditionnel',
    display: "'Libre Caslon Text', Georgia, serif",
    body: "'Libre Franklin', -apple-system, sans-serif",
    gf: 'Libre+Caslon+Text:ital,wght@0,400;1,400&family=Libre+Franklin:wght@400;500'
  },
};

const HERO_LAYOUTS = [
  { key: 'centered', label: 'Centré', sub: 'Botaniques latéraux' },
  { key: 'split', label: 'Divisé', sub: 'Texte & olivier' },
  { key: 'minimal', label: 'Minimal', sub: 'Pur typographique' },
];

const DEFAULT_SECTIONS = ['schedule', 'venue', 'travel', 'rsvp', 'faq'];
const SECTION_LABELS = {
  schedule: 'Le jour J',
  venue: 'Le lieu',
  travel: 'Accès',
  rsvp: 'RSVP',
  faq: 'FAQ'
};

window.PALETTES = PALETTES;
window.FONT_PAIRS = FONT_PAIRS;
window.HERO_LAYOUTS = HERO_LAYOUTS;
window.DEFAULT_SECTIONS = DEFAULT_SECTIONS;
window.SECTION_LABELS = SECTION_LABELS;

// Apply palette to :root
window.applyPalette = (p) => {
  const r = document.documentElement;
  r.style.setProperty('--cream', p.cream);
  r.style.setProperty('--cream-deep', p.creamDeep);
  r.style.setProperty('--ink', p.ink);
  r.style.setProperty('--ink-soft', p.inkSoft);
  r.style.setProperty('--wisteria', p.wisteria);
  r.style.setProperty('--wisteria-soft', p.wisteriaSoft);
  r.style.setProperty('--olive', p.olive);
  r.style.setProperty('--olive-soft', p.oliveSoft);
};

window.applyFonts = (f) => {
  const r = document.documentElement;
  r.style.setProperty('--font-display', f.display);
  r.style.setProperty('--font-body', f.body);
  // inject google font
  const existing = document.getElementById('gf-dynamic');
  if (existing) existing.remove();
  const link = document.createElement('link');
  link.id = 'gf-dynamic';
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${f.gf}&display=swap`;
  document.head.appendChild(link);
};

window.TweaksPanel = ({ state, setState, showSectionOrder = true }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onMsg = (e) => {
      if (e.data?.type === '__activate_edit_mode') setOpen(true);
      if (e.data?.type === '__deactivate_edit_mode') setOpen(false);
    };
    window.addEventListener('message', onMsg);
    window.parent.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const persist = (patch) => {
    const next = { ...state, ...patch };
    setState(next);
    try {
      window.parent.postMessage({ type: '__edit_mode_set_keys', edits: patch }, '*');
    } catch {}
  };

  // drag-and-drop section reordering
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);

  const handleDragStart = (i) => (e) => { setDrag(i); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (i) => (e) => { e.preventDefault(); setOver(i); };
  const handleDrop = (i) => (e) => {
    e.preventDefault();
    if (drag === null || drag === i) return;
    const next = [...state.sectionOrder];
    const [moved] = next.splice(drag, 1);
    next.splice(i, 0, moved);
    persist({ sectionOrder: next });
    setDrag(null); setOver(null);
  };

  return (
    <div className={`tweaks ${open ? 'is-open' : ''}`}>
      <div className="tweaks__head">
        <div className="tweaks__title">Tweaks</div>
        <button className="tweaks__close" onClick={() => setOpen(false)} aria-label="Fermer">×</button>
      </div>

      <div className="tweaks__group">
        <h4>Palette</h4>
        <div className="tweaks__palettes">
          {Object.entries(PALETTES).map(([k, p]) => (
            <button
              key={k}
              className={`tweaks__swatch ${state.palette === k ? 'is-active' : ''}`}
              style={{
                background: p.cream,
                '--swatch-accent': p.wisteria
              }}
              onClick={() => persist({ palette: k })}
              title={p.label}
            >
              <span style={{background: p.olive, width: 8, height: 8, borderRadius: 999, position:'absolute', top: 6, left: 6}}/>
              <span style={{background: p.wisteria, width: 8, height: 8, borderRadius: 999, position:'absolute', top: 6, right: 6}}/>
            </button>
          ))}
        </div>
        <div style={{fontSize: 11, color: 'var(--ink-soft)', marginTop: 8}}>
          {PALETTES[state.palette].label}
        </div>
      </div>

      <div className="tweaks__group">
        <h4>Typographie</h4>
        <div className="tweaks__options">
          {Object.entries(FONT_PAIRS).map(([k, f]) => (
            <button
              key={k}
              className={`tweaks__opt ${state.fonts === k ? 'is-active' : ''}`}
              onClick={() => persist({ fonts: k })}
            >
              {f.label} <small>{f.sub}</small>
            </button>
          ))}
        </div>
      </div>

      <div className="tweaks__group">
        <h4>Mise en page du héros</h4>
        <div className="tweaks__options">
          {HERO_LAYOUTS.map(l => (
            <button
              key={l.key}
              className={`tweaks__opt ${state.hero === l.key ? 'is-active' : ''}`}
              onClick={() => persist({ hero: l.key })}
            >
              {l.label} <small>{l.sub}</small>
            </button>
          ))}
        </div>
      </div>

      {showSectionOrder && (
        <div className="tweaks__group">
          <h4>Ordre des sections</h4>
          <div className="tweaks__sections">
            {state.sectionOrder.map((key, i) => (
              <div
                key={key}
                className={`tweaks__section-row ${drag === i ? 'is-dragging' : ''} ${over === i && drag !== i ? 'is-over' : ''}`}
                draggable
                onDragStart={handleDragStart(i)}
                onDragOver={handleDragOver(i)}
                onDrop={handleDrop(i)}
                onDragEnd={() => { setDrag(null); setOver(null); }}
              >
                <span className="grip">⋮⋮</span>
                <span>{SECTION_LABELS[key]}</span>
              </div>
            ))}
          </div>
          <div style={{fontSize: 10, color: 'var(--ink-soft)', marginTop: 6, letterSpacing: '0.1em'}}>
            GLISSER POUR RÉORDONNER
          </div>
        </div>
      )}
    </div>
  );
};
